// Unified shift status utilities for consistent status handling across the application

export type ShiftStatusType = 
  | 'scheduled' 
  | 'in_progress' 
  | 'completed' 
  | 'cancelled' 
  | 'no_show'
  | 'pending_review'
  | 'approved'
  | 'rejected';

export interface ShiftStatusInfo {
  status: ShiftStatusType;
  label: string;
  color: 'success' | 'warning' | 'error' | 'info' | 'default' | 'primary';
  description: string;
}

/**
 * Get the unified status for any shift-like object
 * This handles both regular Shift objects and ShiftCompletion objects
 */
export function getUnifiedShiftStatus(shiftOrCompletion: any): ShiftStatusType {
  // If this is a ShiftCompletion object with review information
  if ('isReviewedByAdmin' in shiftOrCompletion) {
    if (shiftOrCompletion.isReviewedByAdmin) {
      return shiftOrCompletion.isAcceptedByAdmin ? 'approved' : 'rejected';
    } else if (shiftOrCompletion.isCompletedByStaff) {
      return 'pending_review';
    }
  }
  
  // For regular status field
  if (shiftOrCompletion.status) {
    const status = shiftOrCompletion.status.toLowerCase();
    
    // Handle various status formats
    switch (status) {
      case 'scheduled':
        return 'scheduled';
      case 'in_progress':
      case 'inprogress':
        return 'in_progress';
      case 'completed':
        return 'completed';
      case 'cancelled':
        return 'cancelled';
      case 'no_show':
      case 'noshow':
        return 'no_show';
      case 'pending_review':
      case 'pendingreview':
        return 'pending_review';
      case 'approved':
        return 'approved';
      case 'rejected':
        return 'rejected';
      default:
        return 'scheduled'; // Default fallback
    }
  }
  
  return 'scheduled'; // Default fallback
}

/**
 * Get comprehensive status information including styling
 */
export function getShiftStatusInfo(shiftOrCompletion: any): ShiftStatusInfo {
  const status = getUnifiedShiftStatus(shiftOrCompletion);
  
  switch (status) {
    case 'scheduled':
      return {
        status: 'scheduled',
        label: 'Scheduled',
        color: 'info',
        description: 'Shift is scheduled and waiting to start'
      };
    case 'in_progress':
      return {
        status: 'in_progress',
        label: 'In Progress',
        color: 'warning',
        description: 'Shift is currently active'
      };
    case 'completed':
      return {
        status: 'completed',
        label: 'Completed',
        color: 'success',
        description: 'Shift has been completed'
      };
    case 'cancelled':
      return {
        status: 'cancelled',
        label: 'Cancelled',
        color: 'error',
        description: 'Shift was cancelled'
      };
    case 'no_show':
      return {
        status: 'no_show',
        label: 'No Show',
        color: 'error',
        description: 'Staff did not show up for the shift'
      };
    case 'pending_review':
      return {
        status: 'pending_review',
        label: 'Pending Review',
        color: 'warning',
        description: 'Shift completed and awaiting admin review'
      };
    case 'approved':
      return {
        status: 'approved',
        label: 'Approved',
        color: 'success',
        description: 'Shift completed and approved by admin'
      };
    case 'rejected':
      return {
        status: 'rejected',
        label: 'Rejected',
        color: 'error',
        description: 'Shift completed but rejected by admin'
      };
    default:
      return {
        status: 'scheduled',
        label: 'Unknown',
        color: 'default',
        description: 'Status unknown'
      };
  }
}

/**
 * Get status color for Material-UI Chip component
 */
export function getShiftStatusColor(shiftOrCompletion: any): 'success' | 'warning' | 'error' | 'info' | 'default' | 'primary' {
  return getShiftStatusInfo(shiftOrCompletion).color;
}

/**
 * Get formatted status label
 */
export function getShiftStatusLabel(shiftOrCompletion: any): string {
  return getShiftStatusInfo(shiftOrCompletion).label;
}

/**
 * Check if a shift is in an active state (scheduled or in progress)
 */
export function isShiftActive(shiftOrCompletion: any): boolean {
  const status = getUnifiedShiftStatus(shiftOrCompletion);
  return status === 'scheduled' || status === 'in_progress';
}

/**
 * Check if a shift is completed (any completed state including approved/rejected)
 */
export function isShiftCompleted(shiftOrCompletion: any): boolean {
  const status = getUnifiedShiftStatus(shiftOrCompletion);
  return status === 'completed' || status === 'pending_review' || status === 'approved' || status === 'rejected';
}

/**
 * Check if a shift needs admin review
 */
export function isShiftPendingReview(shiftOrCompletion: any): boolean {
  const status = getUnifiedShiftStatus(shiftOrCompletion);
  return status === 'pending_review';
}

/**
 * Check if a shift has been reviewed by admin
 */
export function isShiftReviewed(shiftOrCompletion: any): boolean {
  const status = getUnifiedShiftStatus(shiftOrCompletion);
  return status === 'approved' || status === 'rejected';
}
